import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import mapboxgl, {
  EventedListener,
  GeoJSONSource,
  Map,
  MapboxGeoJSONFeature,
  MapboxOptions,
  MapLayerMouseEvent,
} from 'mapbox-gl';
import { Box, BoxProps } from 'rebass';
import FreehandMode from '../../helpers/maps/FreehandMode';
import bbox from '@turf/bbox';
import booleanIntersects from '@turf/boolean-intersects';
import booleanContains from '@turf/boolean-contains';
import { BBox, lineString } from '@turf/turf';
import { IdArrayInput } from '../../hooks/useTablePages';
import { useDebouncedCallback } from 'use-debounce';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const { modes } = MapboxDraw.constants;

export type MapPoint = {
  id: string;
  title?: string;
  description?: string;
  coordinates: [longitude: number, latitude: number];
};

type Bounds = [number, number, number, number];

type SelectableMapProps = BoxProps & {
  points: MapPoint[];
  onPointClick?: (point: MapPoint) => void;
  selectedIds?: IdArrayInput;
  onSelectionChange?: (selectedIds: IdArrayInput) => void;
};

const isWithinOrIntersecting = (
  filterPolygon: MapboxGeoJSONFeature,
  feature: MapboxGeoJSONFeature
) => {
  if (feature.geometry.type == 'MultiPolygon') {
    // TODO: Handle multipolygon...
  } else {
    return (
      booleanIntersects(filterPolygon, feature) ||
      booleanContains(filterPolygon, feature)
    );
  }
};

const usBounds: Bounds = [-125.0, 24.396308, -66.93457, 49.384358];

const SelectableMap: React.FC<SelectableMapProps> = ({
  points = [],
  onPointClick,
  onSelectionChange,
  selectedIds,
  ...props
}) => {
  const mapContainer = useRef<HTMLElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [hoveredId, setHoveredId] = useState<string>();
  const drawRef = useRef(
    new MapboxDraw({
      displayControlsDefault: false,
      // Select which mapbox-gl-draw control buttons to add to the map.
      controls: {
        polygon: true,
      },
      modes: {
        ...MapboxDraw.modes,
        draw_polygon: FreehandMode,
      },
      keybindings: false,
      // Set mapbox-gl-draw to draw by default.
      // The user does not have to click the polygon control button first.
      // defaultMode: modes.DRAW_POLYGON,
    })
  );

  const pointsSorted = useMemo(
    () => points.slice().sort((a, b) => a.id.localeCompare(b.id)),
    [points]
  );

  const pointsSortedString = useMemo(
    () => JSON.stringify(pointsSorted),
    [pointsSorted]
  );

  const pointData: {
    type: 'geojson';
    data: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  } = useMemo(() => {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: pointsSorted.map((point) => ({
          id: point.id,
          // feature for Mapbox DC
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: point.coordinates,
          },
          properties: {
            title: point.title,
            description: point.description,
            selected:
              selectedIds && selectedIds.includes(point.id)
                ? // ||
                  // !selectedIds ||
                  // selectedIds.length === 0
                  1
                : 0,
            // hovered: hoveredId && point.id + '' === hoveredId ? 1 : 0,
          },
        })),
      },
    };
  }, [selectedIds, pointsSortedString]);

  const round = (value: number) => Math.round(value * 2000) / 2000;

  const dedupedPointData: {
    type: 'geojson';
    data: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  } = useMemo(() => {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: pointsSorted
          .filter(
            (point, index) =>
              pointsSorted.findIndex(
                (p) =>
                  round(p.coordinates[0]) === round(point.coordinates[0]) &&
                  round(p.coordinates[1]) === round(point.coordinates[1])
              ) === index
          )
          .map((point) => ({
            id: point.id,
            // feature for Mapbox DC
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: point.coordinates,
            },
            properties: {
              title: point.title,
            },
          })),
      },
    };
  }, [points, points.length]);

  const bounds: Bounds | undefined = useMemo(() => {
    if (!pointsSorted || pointsSorted.length < 1) {
      return usBounds;
    }
    if (pointsSorted.length === 1) {
      const coordinates = pointsSorted[0].coordinates;
      const buffer = 0.005;

      return [
        coordinates[0] + buffer,
        coordinates[1] + buffer,
        coordinates[0] - buffer,
        coordinates[1] - buffer,
      ];
    }
    return bbox(
      lineString(
        pointsSorted.map((point) => {
          return point.coordinates;
        })
      )
    ) as Bounds;
  }, [pointsSortedString]);

  useEffect(() => {
    if (!mapRef.current || !bounds) {
      return;
    }
    const map = mapRef.current;
    map.fitBounds(bounds, { padding: 20 });
  }, [mapRef.current, JSON.stringify(bounds)]);

  const onResize = useDebouncedCallback(() => {
    mapRef.current?.resize();
  }, 50);

  useEffect(() => {
    if (mapRef.current || !mapContainer.current) {
      // initialize map only once
      return;
    }
    const options: MapboxOptions = {
      accessToken:
        'pk.eyJ1IjoiZWNvc2Nvbm5lY3QtZGFuIiwiYSI6ImNsbnZ1eW11cjAycDMyanAzMGQ4bDkzcGMifQ.n8NhqBblqKyIjxNWEfLM3w',
      container: mapContainer.current,
      renderWorldCopies: false,
      style: 'mapbox://styles/mapbox/streets-v12',
      // style: 'mapbox://styles/mapbox/navigation-day-v1',
      // style: 'mapbox://styles/mapbox/light-v11',
      bounds,
      fitBoundsOptions: { padding: 20 },
      minZoom: 3,
      maxZoom: 19,
      attributionControl: false,
      // @ts-ignore
      useWebGL2: true,
    };

    // const beforeLayer =  'poi-label';
    const beforeLayer = 'settlement-subdivision-label';

    if (pointsSorted.length === 1) {
      options.center = pointsSorted[0].coordinates;
    }

    if (pointsSorted.length < 2) {
      options.zoom = 13;
    }

    const map: Map = new Map(options);
    mapRef.current = map;

    map.addControl(drawRef.current, 'top-right');
    // Get the control container
    const drawControlContainer = map.getContainer();

    // Find the button element you want to modify (e.g., based on class name)
    const polygonButton = drawControlContainer.querySelector(
      '.mapbox-gl-draw_polygon'
    );
    if (polygonButton) {
      polygonButton.setAttribute('title', 'Lasso Tool');
    }

    map.addControl(
      new mapboxgl.NavigationControl({
        showZoom: true,
        showCompass: false,
      }),
      'top-right'
    );

    map.on('load', () => {
      // const layers = map.getStyle().layers;
      // console.log(layers.map((layer) => layer.id));

      map.addSource('points', pointData);
      map.addSource('unique-points', dedupedPointData);

      const radiusStops = [
        [5, 1],
        [6, 2],
        [12, 4],
        [15, 8],
        [22, 26],
      ];

      const strokeStops = [
        [5, 1],
        [12, 3],
        [15, 4],
        [22, 6],
      ];

      const shadowRadii = radiusStops.map((stop) => [stop[0], stop[1] + 2]);

      map.addLayer(
        {
          id: 'points-shadow',
          type: 'circle',
          source: 'unique-points',
          interactive: false,
          paint: {
            'circle-radius': {
              stops: shadowRadii,
            },
            'circle-color': '#555',
            'circle-blur': 0.9,
            'circle-opacity': {
              stops: [
                [5, 0],
                [6, 0.01],
                [12, 0.05],
                [15, 0.8],
                [22, 0.8],
              ],
            },
            'circle-translate': [3, 6],
          },
        },
        // Place polygons under labels, roads and buildings.
        beforeLayer
      );

      map.addLayer(
        {
          id: 'points',
          type: 'circle',
          source: 'points',
          paint: {
            'circle-radius': {
              stops: radiusStops,
            },
            'circle-color': '#fff',
            'circle-opacity': 0.8,
            'circle-stroke-color': '#0B9FE9',
            'circle-stroke-width': {
              stops: strokeStops,
            },
            'circle-stroke-opacity': 0.9,
          },
          filter: ['==', 'selected', 0],
        },
        // Place polygons under labels, roads and buildings.
        beforeLayer
      );

      // Add a symbol layer
      map.addLayer(
        {
          id: 'points-selected',
          type: 'circle',
          source: 'points',
          paint: {
            'circle-radius': {
              stops: radiusStops,
            },
            'circle-color': '#0B9FE9',
            'circle-opacity': 0.7,
            'circle-stroke-color': '#0B9FE9',
            'circle-stroke-width': {
              stops: [
                [12, 3],
                [15, 3],
                [22, 5],
              ],
            },
            'circle-stroke-opacity': 1,
          },
          filter: ['==', 'selected', 1],
        },
        // Place polygons under labels, roads and buildings.
        beforeLayer
      );

      setMapLoaded(true);
    });

    const resizer = new ResizeObserver(onResize);
    resizer.observe(mapContainer.current);

    return () => {
      mapRef.current = null;
      map.remove();
      resizer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) {
      return;
    }
    const map = mapRef.current;
    const source = map.getSource('points') as GeoJSONSource;

    if (source && pointData.data) {
      source.setData(pointData.data);
    }
  }, [mapRef.current, mapLoaded, pointData]);

  const updateArea: EventedListener = useCallback(
    (e: any) => {
      if (!mapRef.current) {
        return;
      }
      const map = mapRef.current;

      const draw = drawRef.current;
      const data: any = draw.getAll();
      if (!data || !draw || !data.features || !data.features[0]) {
        return;
      }
      const filterPolygon = data.features[0];
      // console.log('filterPolygon', filterPolygon.geometry.coordinates)

      const box: BBox = bbox(filterPolygon);
      // console.log('bbox', box)

      // clear selection
      draw.trash();

      setTimeout(() => {
        draw.changeMode(modes.DRAW_POLYGON);
      });

      const featuresInBBox: MapboxGeoJSONFeature[] = map.queryRenderedFeatures(
        [map.project([box[0], box[1]]), map.project([box[2], box[3]])],
        { layers: ['points', 'points-selected'] }
      );
      // console.log('featuresInBBox', featuresInBBox)

      const filteredFeatures = featuresInBBox.filter((f) =>
        isWithinOrIntersecting(filterPolygon, f)
      );
      // console.log('filteredFeatures', filteredFeatures)

      if (onSelectionChange) {
        const selectedIds = filteredFeatures.map(
          (point) => (point.id + '') as string
        );
        onSelectionChange?.(selectedIds);
      }
      // const fips = filteredFeatures.map((feature) => feature.properties?.FIPS)
      // map.setFilter('counties-highlighted', ['in', 'FIPS', ...fips])
      // console.log('filteredFIPS', fips)
    },
    [mapRef.current, drawRef.current, onSelectionChange]
  );

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    const map = mapRef.current;

    map.on('draw.create', updateArea);
    // map.on('draw.delete', updateArea)
    map.on('draw.update', updateArea);

    return () => {
      map.off('draw.create', updateArea);
      // map.off('draw.delete', updateArea)
      map.off('draw.update', updateArea);
    };
  }, [mapRef.current, onSelectionChange]);

  useEffect(() => {
    if (
      !mapRef.current ||
      !mapLoaded ||
      (!onPointClick && !onSelectionChange)
    ) {
      return;
    }
    const map = mapRef.current;

    const onMouseMove = (e: MapLayerMouseEvent) => {
      if (e.features && e.features.length > 0) {
        setHoveredId(e.features[0].id + '');
        map.getCanvasContainer().style.cursor = 'pointer';
      } else {
        setHoveredId(undefined);
        map.getCanvasContainer().style.cursor = '';
      }
    };
    const onMouseLeave = () => {
      setHoveredId(undefined);
      map.getCanvasContainer().style.cursor = '';
    };

    map.on('mousemove', ['points', 'points-selected'], onMouseMove);
    map.on('mouseleave', ['points', 'points-selected'], onMouseLeave);

    return () => {
      map.off('mousemove', ['points', 'points-selected'], onMouseMove);
      map.off('mouseleave', ['points', 'points-selected'], onMouseLeave);
    };
  }, [mapRef.current, mapLoaded, onPointClick, onSelectionChange]);

  useEffect(() => {
    if (
      !mapRef.current ||
      !mapLoaded ||
      (!onPointClick && !onSelectionChange)
    ) {
      return;
    }
    const map = mapRef.current;

    const onClick = (e: MapLayerMouseEvent) => {
      if (!hoveredId) {
        return;
      }
      const draw = drawRef.current;
      if (draw.getMode() === modes.DRAW_POLYGON || !onPointClick) {
        if (onSelectionChange) {
          const newSelectedIds = (selectedIds || []).slice();
          const currentIndex = newSelectedIds.indexOf(hoveredId);
          if (currentIndex !== -1) {
            newSelectedIds?.splice(currentIndex, 1);
          } else {
            newSelectedIds.push(hoveredId);
          }
          onSelectionChange(newSelectedIds);
        }
        return;
      }
      const point = points.find((p) => p.id === hoveredId);
      if (point) {
        onPointClick(point);
      }
    };

    map.on('click', ['points', 'points-selected'], onClick);

    return () => {
      map.off('click', ['points', 'points-selected'], onClick);
    };
  }, [
    mapRef.current,
    hoveredId,
    mapLoaded,
    onPointClick,
    onSelectionChange,
    selectedIds,
  ]);

  return (
    <Box
      minWidth="200px"
      maxWidth="100%"
      height="100%"
      minHeight="300px"
      width="100%"
      flexGrow={1}
      flexShrink={1}
      {...(props as any)}
      ref={mapContainer}
    />
  );
};

export default SelectableMap;
